import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Autocomplete, Box, TextField, Typography } from "@mui/material";

import {
  RestrictionName,
  SortDirection,
  UsageWindowSize,
  UserRestrictionEntry,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import AIBurndownChart from "./ai-burndown-chart";
import { coalesceToDaily, useUserAIBurndown } from "./use-org-ai-burndown";
import { UsageWindow } from "./use-org-ai-credit";
import { listOrgUserRestrictions } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

// Cap the option list; the picker sorts by usage so the heaviest users surface.
const PICKER_PAGE_SIZE = 100;

const userOptionLabel = (entry: UserRestrictionEntry): string => {
  const { first_name, last_name, email, username } = entry.metadata;
  const fullName = [first_name, last_name].filter(Boolean).join(" ").trim();
  const name = fullName || username || entry.userId;
  return email ? `${name} (${email})` : name;
};

interface UserAIBurndownSectionProps {
  roleId: string;
  regionCode: string;
  usageWindow: UsageWindow;
  ready: boolean;
}

const UserAIBurndownSection = ({
  roleId,
  regionCode,
  usageWindow,
  ready,
}: UserAIBurndownSectionProps) => {
  const { showError } = useContext(SnackbarContext);

  const [users, setUsers] = useState<UserRestrictionEntry[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserRestrictionEntry | null>(
    null,
  );
  const [windowSize, setWindowSize] = useState<UsageWindowSize>(
    UsageWindowSize.DAY,
  );

  const loadUsers = useCallback(async () => {
    try {
      const response = await listOrgUserRestrictions(
        {
          organizationId: toUUID(roleId),
          restrictionName: RestrictionName.AI_CREDIT_USAGE,
          sortDirection: SortDirection.DESC,
          pageNumber: 1,
          pageSize: PICKER_PAGE_SIZE,
          fromTime: usageWindow.from,
          toTime: usageWindow.to,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      setUsers(response.entries);
    } catch (err) {
      showError(err as GrpcError);
    }
  }, [roleId, regionCode, usageWindow, showError]);

  useEffect(() => {
    if (!ready) return;
    loadUsers();
  }, [ready, loadUsers]);

  const {
    isLoading,
    windows: hourlyWindows,
    summary,
  } = useUserAIBurndown(
    selectedUser?.userId ?? "",
    regionCode,
    roleId,
    usageWindow,
    !!selectedUser,
  );

  // Always fetched hourly; coalesce to daily for the daily view (see
  // coalesceToDaily — a DAY-resolution query would drop the first day).
  const windows = useMemo(
    () =>
      windowSize === UsageWindowSize.DAY
        ? coalesceToDaily(hourlyWindows)
        : hourlyWindows,
    [hourlyWindows, windowSize],
  );

  return (
    <Box>
      <Box sx={{ px: "20px", pb: 1.5 }}>
        <Autocomplete
          size="small"
          options={users}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          getOptionLabel={userOptionLabel}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          sx={{ maxWidth: 420 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Choose a user"
              placeholder="Search by name or email"
              inputProps={{
                ...params.inputProps,
                "data-testid": "user-burndown-picker",
              }}
            />
          )}
        />
      </Box>

      {selectedUser ? (
        <AIBurndownChart
          windows={windows}
          windowSize={windowSize}
          onWindowSizeChange={setWindowSize}
          isLoading={isLoading}
          summary={summary}
        />
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: "20px", pb: 2 }}
        >
          Select a user to see their Free vs Paid credit burndown.
        </Typography>
      )}
    </Box>
  );
};

export default UserAIBurndownSection;
